import {
  Breakpoint,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  styled,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useFormikContext } from "formik";
import Loader from "../loader/Loader";

type SubmitModalWithEditFormikProps = {
  title: string;
  children: React.ReactNode;
  closeSubmitModal: CallableFunction;
  childErrorMessage?: string;
  allowSubmit: boolean;
  size: false | Breakpoint | undefined;
  isLoading?: boolean;
  submitButtonName?: string;
  closeModalAfterSubmit?: boolean;
  showSubmitButton?: boolean;
  isEditable: boolean;
  setEditable: any;
};

export interface ModalTitleProps {
  id: string;
  children?: React.ReactNode;
  onClose: () => void;
}

const BootstrapModal = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

function BootstrapModalTitle(props: ModalTitleProps) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle
      sx={{
        m: 0,
        p: 2,
        backgroundColor: "primary.main",
        color: (theme) => theme.palette.grey[50],
      }}
      {...other}
    >
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[50],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
}

const SubmitModalWithEditWithFormik = (
  Props: SubmitModalWithEditFormikProps
) => {
  const {
    title,
    children,
    closeSubmitModal,
    childErrorMessage,
    size,
    isLoading = false,
    submitButtonName = "Submit",
    closeModalAfterSubmit = true,
    showSubmitButton = true,
    isEditable,
    setEditable,
  } = Props;

  const { submitForm, isValid, isSubmitting, touched } = useFormikContext();

  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    let isFunctionRan = true;
    if (isFunctionRan) {
      if (childErrorMessage) {
        toast.error(childErrorMessage);
      }
    }
    return () => {
      isFunctionRan = false;
    };
  }, [childErrorMessage]);

  const _closeSubmitModal = () => {
    setIsOpen(false);
    closeSubmitModal();
  };

  const handleClickCloseModal = () => {
    if (isEditable) {
      setEditable(false);
      return;
    }
    _closeSubmitModal();
  };

  const handleSubmit = async () => {
    await submitForm();
    if (closeModalAfterSubmit && !isSubmitting) {
      _closeSubmitModal();
    }
  };

  const handleEdit = () => {
    setEditable(!isEditable);
  };

  return (
    <BootstrapModal
      open={isOpen}
      onClose={handleClickCloseModal}
      aria-labelledby="customized-dialog-title"
      maxWidth={size}
      fullWidth
    >
      <BootstrapModalTitle
        id="customized-dialog-title"
        onClose={_closeSubmitModal}
      >
        <div>{title}</div>
      </BootstrapModalTitle>
      <DialogContent dividers>
        <div>{children}</div>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          size="small"
          onClick={handleEdit}
          disabled={isEditable}
        >
          Edit
        </Button>
        {showSubmitButton && (
          <Button
            variant="contained"
            size="small"
            disabled={!isEditable || !isValid}
            onClick={handleSubmit}
          >
            {submitButtonName}
          </Button>
        )}
        <Button variant="outlined" onClick={handleClickCloseModal} size="small">
          Cancel
        </Button>
      </DialogActions>
      <Loader isLoading={isSubmitting || isLoading} />
    </BootstrapModal>
  );
};

export default SubmitModalWithEditWithFormik;
